import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import AdminManageMenu from "../components/AdminManageMenu";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import { useTaxonomy } from "../hooks/useTaxonomy";
import {
  Handshake, Search, MapPin, ExternalLink, Globe, Phone, Mail,
  Tag, Star, BadgePercent, Plus, Shield
} from "lucide-react";

// Fallback used only when the live taxonomy fetch is empty. Canonical
// source is the founder-managed `partner_specialties` taxonomy.
const FALLBACK_SPECIALTY_LABELS = {
  financial_advisor: "Financial Advisor", accountant: "Accountant",
  tax_specialist: "Tax Specialist", insurance: "Insurance Specialist",
  legal: "Legal / Lawyer", real_estate: "Real Estate",
  investment: "Investment Advisor", bookkeeper: "Bookkeeper",
  practice_consultant: "Practice Consultant", wellness: "Wellness / Mental Health",
  other: "Other"
};

const SPECIALTY_COLORS = {
  financial_advisor: "#6B4E8A", accountant: "#10B981", tax_specialist: "#8DB5A0",
  insurance: "#3B82F6", legal: "#8B5CF6", real_estate: "#8DB5A0",
  investment: "#6366F1", bookkeeper: "#10B981", practice_consultant: "#8DB5A0",
  wellness: "#F0ECF7", other: "#64748B"
};

const PartnersPage = () => {
  const { user } = useAuth();
  const { items: specialtyItems, refetch: refetchSpecialties } = useTaxonomy("partner_specialties");
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSpecialty, setFilterSpecialty] = useState("");
  const [filterProvince, setFilterProvince] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const isAdmin = user?.is_admin;

  // Live-bound label map. Falls back to the static seed so the page
  // always renders even when the API call is in flight.
  const SPECIALTY_LABELS = useMemo(() => {
    const map = { ...FALLBACK_SPECIALTY_LABELS };
    (specialtyItems || []).forEach((it) => {
      if (it.key) map[it.key] = it.label || map[it.key] || it.key;
    });
    return map;
  }, [specialtyItems]);

  // Ordered options for <Select> dropdowns. Empty taxonomy → use fallback.
  const specialtyOptions = useMemo(() => {
    if (specialtyItems && specialtyItems.length > 0) {
      return specialtyItems.map((it) => ({ key: it.key, label: it.label }));
    }
    return Object.entries(FALLBACK_SPECIALTY_LABELS).map(([key, label]) => ({ key, label }));
  }, [specialtyItems]);

  // Admin add partner
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    website: "", specialty: "financial_advisor", location: "",
    province: "AB", description: "", member_discount: ""
  });

  useEffect(() => {
    fetchPartners();
    axios.get(`${API}/provinces`).then(r => setProvinces(r.data)).catch(() => {});
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterSpecialty) params.append("specialty", filterSpecialty);
      if (filterProvince) params.append("province", filterProvince);
      const res = await axios.get(`${API}/partners?${params}`);
      setPartners(res.data);
    } catch { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPartners(); }, [filterSpecialty, filterProvince]);

  const handleSearch = (e) => { e.preventDefault(); fetchPartners(); };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.description) {
      toast.error("Fill in required fields"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/partners/add`, form);
      toast.success("Partner added!");
      setShowAdd(false);
      setForm({ business_name: "", contact_name: "", email: "", phone: "", website: "", specialty: "financial_advisor", location: "", province: "AB", description: "", member_discount: "" });
      fetchPartners();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="partners-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <EditableText blockKey="partners.title" fallback="Preferred Partners" as="span" />
              <TaxonomyManager
                kind="partner_specialties"
                title="Partner specialties"
                description="Manage the specialty list partners can be filed under."
                triggerLabel="Manage partner specialties"
                onChange={refetchSpecialties}
              />
            </h1>
            <EditableText
              blockKey="partners.subtitle"
              fallback="Trusted professionals recommended for dental hygienists"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          {isAdmin && (
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild>
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="add-partner-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Partner
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Preferred Partner</DialogTitle></DialogHeader>
                <form onSubmit={handleAddPartner} className="space-y-4 mt-4">
                  <div><Label>Business Name *</Label><Input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="partner-name-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Contact Name *</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                    <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                    <div><Label>Website</Label><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..." className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Specialty</Label>
                      <Select value={form.specialty} onValueChange={v => setForm({...form, specialty: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]" data-testid="partner-specialty-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{specialtyOptions.map(opt => <SelectItem key={opt.key} value={opt.key} data-testid={`partner-specialty-${opt.key}`}>{opt.label}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div><Label>Province</Label>
                      <Select value={form.province} onValueChange={v => setForm({...form, province: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                        <SelectContent>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Town / City" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div><Label>Description *</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Services, specializations, experience..." className="mt-2 bg-white border-[#E5DFEC] min-h-[100px]" /></div>
                  <div><Label>DHDH Member Discount</Label><Input value={form.member_discount} onChange={e => setForm({...form, member_discount: e.target.value})} placeholder="e.g. 15% off first consultation" className="mt-2 bg-white border-[#E5DFEC]" data-testid="partner-discount-input" /></div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowAdd(false)} className="rounded-full">Cancel</Button>
                    <Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" disabled={submitting} data-testid="submit-partner-btn">{submitting ? "Adding..." : "Add Partner"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <form onSubmit={handleSearch} className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search partners..." className="pl-10 bg-white border-[#E5DFEC]" data-testid="partner-search" />
          </form>
          <Select value={filterSpecialty || "all"} onValueChange={v => setFilterSpecialty(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 bg-white border-[#E5DFEC]" data-testid="partner-filter-specialty-trigger"><SelectValue placeholder="Specialty" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Specialties</SelectItem>{specialtyOptions.map(opt => <SelectItem key={opt.key} value={opt.key} data-testid={`partner-filter-specialty-${opt.key}`}>{opt.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterProvince || "all"} onValueChange={v => setFilterProvince(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-white border-[#E5DFEC]"><SelectValue placeholder="Province" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Provinces</SelectItem>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Partner Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-44 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : partners.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {partners.map((partner, idx) => (
              <motion.div
                key={partner.partner_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className={`relative bg-white border rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer ${partner.is_featured ? 'border-[#8DB5A0] ring-1 ring-[#8DB5A0]/20' : 'border-[#E5DFEC]'}`}
                onClick={() => setSelectedPartner(partner)}
              >
                <div className="absolute top-2 right-2 z-10">
                  <AdminManageMenu
                    resourceType="partner"
                    resourceId={partner.partner_id}
                    resourceLabel={partner.name}
                    onDeleted={() => fetchPartners && fetchPartners()}
                  />
                </div>
                {partner.is_featured && (
                  <div className="flex items-center gap-1 text-xs text-[#8DB5A0] font-medium mb-2">
                    <Star className="w-3 h-3 fill-[#8DB5A0]" /> Featured Partner
                  </div>
                )}
                {/* Vetted-by-DHDH trust pill (every approved partner) */}
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#6B4E8A]/10 text-[#6B4E8A] border border-[#6B4E8A]/20" data-testid={`partner-vetted-${partner.partner_id}`}>
                    <Shield className="w-3 h-3" /> Vetted by DHDH
                  </span>
                  {partner.created_at && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#94A3B8]">
                      · Partner since {new Date(partner.created_at).getFullYear()}
                    </span>
                  )}
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${SPECIALTY_COLORS[partner.specialty] || "#64748B"}15` }}>
                    <Handshake className="w-6 h-6" style={{ color: SPECIALTY_COLORS[partner.specialty] || "#64748B" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#6B4E8A] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{partner.business_name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${SPECIALTY_COLORS[partner.specialty] || "#64748B"}15`, color: SPECIALTY_COLORS[partner.specialty] || "#64748B" }}>
                      {SPECIALTY_LABELS[partner.specialty] || partner.specialty}
                    </span>
                    <div className="flex items-center gap-3 mt-2 text-sm text-[#64748B]">
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{partner.location || partner.province}</span>
                    </div>
                    <p className="text-sm text-[#64748B] mt-2 line-clamp-2">{partner.description}</p>
                    {partner.member_discount && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-[#10B981] bg-[#10B981]/10 px-2 py-1 rounded-full w-fit">
                        <BadgePercent className="w-3 h-3" /> {partner.member_discount}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
            <Handshake className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
            <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No partners listed yet</h3>
            <p className="text-[#64748B]">Check back soon for trusted professional recommendations</p>
          </div>
        )}

        {/* Partner Detail Dialog */}
        <Dialog open={!!selectedPartner} onOpenChange={() => setSelectedPartner(null)}>
          <DialogContent className="max-w-md">
            {selectedPartner && (
              <>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedPartner.business_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <span className="inline-block text-sm px-3 py-1 rounded-full" style={{ backgroundColor: `${SPECIALTY_COLORS[selectedPartner.specialty] || "#64748B"}15`, color: SPECIALTY_COLORS[selectedPartner.specialty] || "#64748B" }}>
                    {SPECIALTY_LABELS[selectedPartner.specialty] || selectedPartner.specialty}
                  </span>
                  <p className="text-sm text-[#6B4E8A]">{selectedPartner.description}</p>
                  {selectedPartner.member_discount && (
                    <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-3 flex items-center gap-2">
                      <BadgePercent className="w-5 h-5 text-[#10B981]" />
                      <div>
                        <p className="text-sm font-medium text-[#10B981]">DHDH Member Discount</p>
                        <p className="text-sm text-[#6B4E8A]">{selectedPartner.member_discount}</p>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-[#64748B]"><MapPin className="w-4 h-4" />{selectedPartner.location}, {selectedPartner.province}</div>
                    {selectedPartner.email && <div className="flex items-center gap-2 text-[#64748B]"><Mail className="w-4 h-4" />{selectedPartner.email}</div>}
                    {selectedPartner.phone && <div className="flex items-center gap-2 text-[#64748B]"><Phone className="w-4 h-4" />{selectedPartner.phone}</div>}
                  </div>
                  {selectedPartner.website && (
                    <a href={selectedPartner.website.startsWith("http") ? selectedPartner.website : `https://${selectedPartner.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#6B4E8A] text-white py-3 rounded-full hover:bg-[#553D6F] transition-colors font-medium">
                      <Globe className="w-4 h-4" /> Visit Website
                    </a>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PartnersPage;
